/* Mobile PDP adapter for the existing QuickAddDrawer, VariantPicker and ProductForm. */
(() => {
  if (customElements.get('haiyi-pdp-quick-add')) return;
  class HaiyiPdpQuickAdd extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.section = this.closest('.cc-main-product');
      this.root = this.section.querySelector('[data-hp-main]');
      this.strings = JSON.parse(this.querySelector('[data-hq-strings]').textContent);
      this.mobile = matchMedia('(max-width: 768px)');
      this.events = new AbortController();
      const opts = {signal: this.events.signal};
      this.sequence = 0;
      this.mainPending = false;
      this.adding = false;
      this.section.addEventListener('on:variant:change', () => this.schedule(), opts);
      this.section.addEventListener('input', () => this.schedule(), opts);
      this.mobile.addEventListener('change', () => {
        if (!this.mobile.matches && this.drawer?.hqPdp === this) {
          this.drawer.close();
          this.releaseDrawer();
        }
        this.schedule();
      }, opts);
      window.addEventListener('scroll', () => this.visibility(), {...opts, passive: true});
      window.addEventListener('resize', () => this.schedule(), opts);
      for (const type of ['on:cart-drawer:before-open', 'on:cart-drawer:after-close', 'on:quickbuy:after-close']) {
        document.addEventListener(type, () => this.schedule(), opts);
      }
      this.mutation = new MutationObserver(records => {
        if (records.some(r => !r.target.closest?.('[data-hq-bar],haiyi-pdp-quick-add'))) this.schedule();
      });
      this.mutation.observe(this.section, {childList: true, subtree: true});
      this.schedule();
    }
    disconnectedCallback() {
      if (this.drawer?.hqPdp === this) { this.drawer.close(); this.releaseDrawer(); }
      this.events?.abort(); this.events = null;
      this.mutation?.disconnect(); cancelAnimationFrame(this.frame);
      this.hideToast(); this.toast?.remove();
      document.documentElement.classList.remove('hq-bar-on', 'hq-sheet-on');
    }
    get picker() { return this.root.querySelector('variant-picker'); }
    get form() { return this.root.querySelector('product-form'); }
    get quantity() { return this.form?.querySelector('[name="quantity"]'); }
    variant(container = this.root) {
      try { return JSON.parse(container.querySelector('[data-hp-variant]')?.dataset.hpVariant || 'null'); }
      catch { return null; }
    }
    money(cents, digits = 2) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {style: 'currency', currency: this.dataset.currency, minimumFractionDigits: digits, maximumFractionDigits: digits}).format(cents / 100);
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.refreshBar(); this.renderSheet(); this.visibility(); });
    }
    refreshBar() {
      const bar = this.section.querySelector('sticky-atc-panel');
      if (!bar || !this.picker || !this.form) return;
      this.bar = bar;
      let ui = bar.querySelector('[data-hq-bar]');
      if (!ui) {
        ui = document.createElement('div'); ui.dataset.hqBar = ''; ui.className = 'hq-bar__inner';
        const img = document.createElement('img'); img.alt = ''; img.className = 'hq-bar__img';
        const info = document.createElement('div'); info.className = 'hq-bar__info';
        const title = document.createElement('p'); title.className = 'hq-bar__title'; title.textContent = this.dataset.productTitle;
        const meta = document.createElement('div'); meta.className = 'hq-bar__meta';
        const price = document.createElement('b'); price.className = 'hq-bar__price';
        const selected = document.createElement('span'); selected.className = 'hq-bar__selected';
        meta.append(price, selected); info.append(title, meta);
        const a = document.createElement('a'); a.className = 'hq-bar__button'; a.href = '#variants';
        a.dataset.hqPdp = ''; a.setAttribute('aria-haspopup', 'dialog'); a.textContent = this.strings.choose;
        ui.append(img, info, a); bar.append(ui);
      }
      bar.classList.add('hq-bar-ready');
      const v = this.variant();
      const nativeImage = this.root.querySelector('.media-gallery img');
      const src = v?.featured_image?.src || nativeImage?.currentSrc;
      const img = ui.querySelector('img'); img.hidden = !src;
      if (src && img.getAttribute('src') !== src) img.src = src;
      const price = ui.querySelector('.hq-bar__price');
      price.textContent = v ? this.money(v.price) : `${this.strings.from} ${this.root.querySelector('.price__current')?.textContent.trim() || ''}`;
      ui.querySelector('.hq-bar__selected').textContent = v?.public_title || v?.title || this.strings.choose;
      ui.querySelector('a').setAttribute('aria-disabled', String((this.mainPending && !this.variantError) || this.adding));
    }
    visibility() {
      if (!this.bar) return;
      const footer = document.querySelector('footer') || document.querySelector('.shopify-section-group-footer-group');
      const blocked = this.drawer?.hasAttribute('open') || document.querySelector('cart-drawer[open]') || document.querySelector('.drawer[open]:not(.hq-pdp-sheet)');
      const show = this.mobile.matches && !this.adding && !blocked && !this.toast?.classList.contains('is-on')
        && this.form?.getBoundingClientRect().bottom < 0 && (!footer || footer.getBoundingClientRect().top >= innerHeight);
      this.bar.classList.toggle('hq-bar-visible', !!show);
      document.documentElement.classList.toggle('hq-bar-on', !!show || !!this.toast?.classList.contains('is-on'));
    }
    beginVariant(picker) {
      this.pendingOptionIds = picker?.getSelectedOptionIds();
      this.variantError = false;
      this.mainPending = true; this.uncertain = false;
      this.sequence += 1; this.schedule();
      const d = this.drawer;
      if (d?.hqPdp === this) d.querySelector('[name="add"]')?.setAttribute('aria-disabled', 'true');
      return this.sequence;
    }
    isCurrent(seq) { return seq === this.sequence && this.isConnected; }
    finishVariant() {
      this.variantError = false;
      this.mainPending = false;
      this.drawer?.querySelector('[name="add"]')?.removeAttribute('aria-disabled');
      this.schedule();
    }
    failVariant(seq) {
      if (!this.isCurrent(seq)) return;
      this.mainPending = true; this.variantError = true;
      this.error(this.strings.loadError); this.schedule();
    }
    applyMain(html, productChange) {
      if (!html) throw Error('Missing product section');
      const picker = this.picker;
      const ids = this.drawer.querySelector('variant-picker').getSelectedOptionIds();
      picker.optionSelectors.forEach((group, index) => {
        group.querySelectorAll('input.opt-btn').forEach(input => { input.checked = input.dataset.valueId === ids[index]; });
        group.querySelectorAll('.custom-select__option').forEach(input => input.setAttribute('aria-selected', String(input.dataset.valueId === ids[index])));
      });
      picker.updateContent(this.section, html, productChange);
      this.picker?.announceChange();
    }
    async open(drawer, opener) {
      if (!this.mobile.matches || (this.mainPending && !this.variantError) || this.adding || drawer.hasAttribute('open')) return;
      this.hideToast();
      this.drawer = drawer; drawer.hqPdp = this;
      this.scrollPosition = scrollY;
      this.qty = Number(this.quantity?.value) || 1;
      this.uncertain = false;
      drawer.classList.add('hq-pdp-sheet', 'is-loading');
      drawer.setAttribute('data-hq-product', this.root.dataset.productId);
      document.documentElement.classList.add('hq-sheet-on');
      this.originalUpdateUrl = this.picker.dataset.updateUrl;
      this.picker.dataset.updateUrl = 'false';
      drawer.content.innerHTML = ''; drawer.footerContent.innerHTML = '';
      drawer.notification.hidden = true;
      const url = new URL(this.dataset.productUrl, location.origin);
      const v = this.variant();
      if (this.variantError && this.pendingOptionIds?.every(Boolean)) url.searchParams.set('option_values', this.pendingOptionIds.join(','));
      else if (v) url.searchParams.set('variant', v.id);
      drawer.productUrl = url.pathname + url.search;
      const generation = this.openGeneration = (this.openGeneration || 0) + 1;
      SideDrawer.prototype.open.call(drawer, opener);
      this.decorate();
      this.visibility();
      try {
        const response = await fetch(url, {headers: {Accept: 'text/html'}});
        if (!response.ok) throw Error('Product load failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (drawer.hqPdp !== this || !drawer.hasAttribute('open') || generation !== this.openGeneration) return;
        const template = doc.getElementById('quick-buy-template');
        if (!template) throw Error('Missing quick buy template');
        drawer.productEl = template.content.querySelector('.js-product').cloneNode(true);
        drawer.renderProduct(opener);
        if (this.variantError) { this.applyMain(doc.getElementById(this.section.id), false); this.finishVariant(); }
        const quantity = drawer.querySelector('[name="quantity"]');
        if (quantity) quantity.value = this.qty;
        this.decorate(); this.renderSheet();
      } catch {
        drawer.content.textContent = this.strings.loadError;
        drawer.classList.remove('is-loading');
      }
    }
    close(drawer) {
      drawer.opener = null;
      SideDrawer.prototype.close.call(drawer, () => { drawer.backBtn.hidden = true; });
      window.scrollTo({top: this.scrollPosition, behavior: 'instant'});
      this.refreshBar(); this.visibility();
      this.bar?.querySelector('[data-hq-pdp]')?.focus({preventScroll: true});
      requestAnimationFrame(() => window.scrollTo({top: this.scrollPosition, behavior: 'instant'}));
    }
    decorate() {
      const d = this.drawer;
      if (!d || d.hqPdp !== this) return;
      if (!d.querySelector('[data-hq-handle]')) {
        const handle = document.createElement('div'); handle.dataset.hqHandle = ''; handle.className = 'hq-sheet-handle';
        const line = document.createElement('span'); handle.append(line); handle.setAttribute('aria-hidden', 'true'); d.prepend(handle);
        let start;
        handle.addEventListener('touchstart', e => { start = e.touches[0].clientY; }, {passive: true});
        handle.addEventListener('touchend', e => { if (start !== undefined && e.changedTouches[0].clientY - start > 90) d.close(); start = undefined; }, {passive: true});
      }
      if (!d.querySelector('[data-hq-close]')) {
        const close = d.querySelector('.quick-add__close-btn--mob').cloneNode(true);
        close.dataset.hqClose = ''; d.append(close);
      }
      if (!this.drawerEvents) {
        this.drawerEvents = new AbortController();
        const opt = {signal: this.drawerEvents.signal};
        const syncQuantity = e => {
          if (e.target.name === 'quantity') {
            if (this.quantity) this.quantity.value = e.target.value;
            this.quantity?.dispatchEvent(new Event('input', {bubbles: true}));
            this.renderSheet();
          }
        };
        d.addEventListener('input', syncQuantity, opt);
        d.addEventListener('change', syncQuantity, opt);
        d.addEventListener('on:variant:change', () => this.schedule(), opt);
        d.addEventListener('on:quickbuy:after-close', () => { if (!d.hasAttribute('open')) this.releaseDrawer(); }, opt);
        d.addEventListener('on:quickbuy:before-close', () => {
          document.documentElement.classList.remove('hq-sheet-on');
          this.schedule();
        }, opt);
      }
    }
    renderSheet() {
      const d = this.drawer;
      if (!d?.hasAttribute('open') || d.hqPdp !== this || d.classList.contains('is-loading')) return;
      const v = this.variant(d);
      const qty = Number(d.querySelector('[name="quantity"]')?.value) || 1;
      const button = d.querySelector('[name="add"]');
      if (button) {
        button.disabled = !v?.available || this.mainPending || this.adding || this.uncertain;
        if (v?.available && !this.adding) button.textContent = `${button.dataset.addToCartText} · ${this.money(v.price * qty)}`;
      }
      const n = (v?.options || []).map(x => String(x).match(/^(\d[\d,]*)\s*seeds?$/i)).filter(Boolean);
      const count = n.length === 1 ? Number(n[0][1].replace(/,/g, '')) : 0;
      d.querySelectorAll('[data-hp-option-count]').forEach(el => {
        const seed = Number(el.dataset.hpOptionCount.match(/[\d,]+/)?.[0].replace(/,/g, ''));
        const price = Number(el.dataset.hpOptionPrice);
        if (seed && Number.isFinite(price)) el.textContent = `${this.money(price / seed, 3)} / seed`;
      });
      d.querySelectorAll('[data-hp-unit]').forEach(el => { el.hidden = !count; el.textContent = count ? `${this.money(v.price / count, 3)} / seed` : ''; });
      d.querySelectorAll('.option-selector:has(.js-color-text)').forEach(group => {
        const inputs = [...group.querySelectorAll('input.opt-btn')];
        if (inputs.length <= 12) return;
        if (!group.querySelector('[data-hq-more]')) {
          const more = document.createElement('button'); more.type = 'button'; more.dataset.hqMore = '';
          more.addEventListener('click', () => { group.classList.toggle('hq-expanded'); this.renderSheet(); }); group.append(more);
        }
        if (inputs.some((el,i) => i >= 12 && el.checked)) group.classList.add('hq-expanded');
        const expanded = group.classList.contains('hq-expanded');
        inputs.forEach((input,i) => { input.hidden = i >= 12 && !expanded; input.nextElementSibling?.classList.toggle('hq-extra-swatch', i >= 12 && !expanded); });
        const more = group.querySelector('[data-hq-more]'); more.setAttribute('aria-expanded', String(expanded));
        more.textContent = expanded ? this.strings.less : this.strings.more.replace('[count]', inputs.length);
      });
      // Use the existing product receipt copy, without inventing product-specific facts.
      const source = this.root.querySelector('.hp-offer-receive');
      if (source) {
        let receipt = d.querySelector('[data-hq-receipt]');
        if (!receipt) { receipt = document.createElement('div'); receipt.dataset.hqReceipt = ''; d.content.append(receipt); }
        if (receipt.dataset.variant !== String(v?.id)) { receipt.innerHTML = source.innerHTML; receipt.dataset.variant = String(v?.id); }
      }
    }
    releaseDrawer() {
      const d = this.drawer;
      if (!d || d.hqPdp !== this || this.adding) return;
      // Local PDP content is never a native card cache. Remove it before desktop/card reuse.
      d.content.replaceChildren(); d.footerContent.replaceChildren(); d.productEl = null;
      this.drawerEvents?.abort(); this.drawerEvents = null;
      d.classList.remove('hq-pdp-sheet'); d.removeAttribute('data-hq-product');
      d.querySelectorAll('[data-hq-handle],[data-hq-close],[data-hq-receipt]').forEach(el => el.remove());
      d.hqPdp = null; d.productUrl = null;
      this.picker?.setAttribute('data-update-url', this.originalUpdateUrl || 'false');
      document.documentElement.classList.remove('hq-sheet-on');
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        window.scrollTo({top: this.scrollPosition, behavior: 'instant'});
        if (this.mobile.matches) this.bar?.querySelector('[data-hq-pdp]')?.focus({preventScroll:true});
      });
      this.schedule();
    }
    error(message) {
      const form = this.drawer?.querySelector('product-form');
      form?.setErrorMsgState(message);
      const error = form?.querySelector('.js-form-error');
      if (error && /cart/.test(message)) {
        const link = document.createElement('a'); link.href = theme.routes.cart; link.textContent = ` ${this.strings.viewCart}`; error.append(link);
      }
      this.renderSheet();
    }
    async submit(productForm) {
      if (this.adding || this.mainPending || this.uncertain) return;
      const form = productForm.querySelector('form');
      const v = this.variant(this.drawer);
      if (!v?.available || !form.reportValidity()) return;
      const fd = new FormData(form), id = Number(fd.get('id')), qty = Number(fd.get('quantity') || 1);
      if (id !== v.id || !Number.isInteger(qty) || qty < 1) return;
      this.adding = true; this.renderSheet();
      productForm.setErrorMsgState();
      const button = productForm.querySelector('[name="add"]'); button.setAttribute('aria-disabled', 'true'); button.classList.add('is-loading');
      const lock = [...this.drawer.querySelectorAll('input:not([type="hidden"]),select,quantity-input button,.opt-label,custom-select button')];
      lock.forEach(el => { if ('disabled' in el) { el.dataset.hqWasDisabled = String(el.disabled); el.disabled = true; } else el.style.pointerEvents = 'none'; });
      let before, result, posted = false, confirmed = false;
      const cartDrawer = document.querySelector('cart-drawer');
      const route = `${window.Shopify.routes.root}cart.js`;
      try {
        const r = await fetch(route, {cache: 'no-store'}); if (!r.ok) throw Error('cart read'); before = await r.json();
        let sections = 'cart-icon-bubble'; if (cartDrawer) sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
        fd.set('sections', sections); fd.set('sections_url', location.pathname);
        posted = true;
        const response = await fetch(theme.routes.cartAdd, {signal: AbortSignal.timeout(25000), method: 'POST', headers: {Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest'}, body: fd});
        result = await response.json();
        if (!response.ok || Number(result.status) >= 400) { posted = response.status >= 500 || Number(result.status) >= 500; throw Error(result.description || result.message || this.strings.uncertain); }
        confirmed = true;
        productForm.constructor.updateCartIcon(result);
        if (cartDrawer) await cartDrawer.renderContents(result, false);
        const read = await fetch(route, {cache: 'no-store'}); if (!read.ok) throw Error('cart refresh');
        const cart = await read.json();
        const previous = before.items.find(item => item.variant_id === id);
        productForm.dispatchEvent(new CustomEvent(previous ? 'on:line-item:change' : 'on:cart:add', {
          bubbles: true,
          detail: previous ? {cart, variantId: id, oldQuantity: previous.quantity, newQuantity: result.quantity} : {cart, variantId: id}
        }));
        this.success(v, qty);
      } catch (error) {
        if (confirmed) { this.error(this.strings.refreshError); this.uncertain = true; }
        else if (posted) {
          // Never replay an ambiguous POST. A fresh read can confirm the exact expected delta.
          try {
            const r = await fetch(route, {cache:'no-store'}); if (!r.ok) throw Error('cart read'); const cart = await r.json();
            const sum = c => c.items.filter(x => x.variant_id === id && !x.selling_plan_allocation && !Object.keys(x.properties || {}).length).reduce((n,x) => n+x.quantity,0);
            if (before && sum(cart) === sum(before) + qty) { this.success(v, qty); confirmed = true; cartDrawer?.refresh(false); }
            else { this.uncertain = true; this.error(this.strings.uncertain); }
          } catch { this.uncertain = true; this.error(this.strings.uncertain); }
        } else this.error(error.message === 'cart read' ? this.strings.uncertain : error.message);
      } finally {
        this.adding = false;
        lock.forEach(el => { if ('disabled' in el) { el.disabled = el.dataset.hqWasDisabled === 'true'; delete el.dataset.hqWasDisabled; } else el.style.pointerEvents = ''; });
        button.removeAttribute('aria-disabled'); button.classList.remove('is-loading');
        if (!this.drawer?.hasAttribute('open')) this.releaseDrawer();
        this.schedule();
      }
    }
    success(variant, qty) {
      this.drawer.close();
      this.hideToast();
      if (!this.toast) {
        const toast = document.createElement('div'); toast.className = 'hq-pdp-toast'; toast.setAttribute('role', 'status');
        const copy = document.createElement('div'); copy.append(document.createElement('b'), document.createElement('span'));
        const view = document.createElement('a'); view.href = theme.routes.cart; view.textContent = this.strings.viewCart;
        view.addEventListener('click', async e => {
          const cart = document.querySelector('cart-drawer');
          if (cart) { e.preventDefault(); this.hideToast(); await cart.refresh(false); cart.open(view); }
        });
        toast.append(copy, view); this.append(toast); this.toast = toast;
        toast.addEventListener('mouseenter', () => this.pauseToast()); toast.addEventListener('focusin', () => this.pauseToast());
        toast.addEventListener('mouseleave', () => this.resumeToast()); toast.addEventListener('focusout', () => this.resumeToast());
      }
      this.toast.querySelector('b').textContent = this.strings.added;
      this.toast.querySelector('span').textContent = `${variant.public_title || variant.title} × ${qty}`;
      this.toast.classList.add('is-on'); this.remaining = 6000; this.resumeToast(); this.visibility();
    }
    pauseToast() { if (this.toastTimer) { clearTimeout(this.toastTimer); this.toastTimer = null; this.remaining = Math.max(0,this.remaining-(performance.now()-this.started)); } }
    resumeToast() {
      if (!this.toast?.classList.contains('is-on') || this.toast.matches(':hover,:focus-within')) return;
      this.pauseToast(); this.started = performance.now(); this.toastTimer = setTimeout(() => this.hideToast(), this.remaining);
    }
    hideToast() { clearTimeout(this.toastTimer); this.toastTimer = null; this.toast?.classList.remove('is-on'); this.visibility(); }
  }
  customElements.define('haiyi-pdp-quick-add', HaiyiPdpQuickAdd);
})();
